import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@app/router/constants';
import { selectIsFiberServiceable, selectWorkOrders } from '@app/redux';
import {
  getContractInstance,
  selectContractInstance,
  selectEcaError,
  selectEcaIsLoading,
  selectEcaModalType,
  selectHasSeenSuccessScreen,
  setEcaError,
  setEcaIsLoading,
  setEcaModalType,
  setHasSeenSuccessScreen,
} from '@app/redux/electronicCustomerAgreement';
import { ContractStateReason, EcaModalType } from '@app/redux/electronicCustomerAgreement/types';
import { selectingPendingInstallation } from '@app/redux/pendingInstallation';

import { useNavigate } from '../../hooks/useNavigate';
import { ReactComponent as Illustration } from './assets/homeOffice.svg';
import { CancelSignEcaModal } from './components/CancelSignEcaModal';
import { EcaErrorModal } from './components/EcaErrorModal';
import { EcaLoadingState } from './components/EcaLoadingState/EcaLoadingState';
import { EcaSuccessModal } from './components/EcaSuccessModal';
import { iframeProps } from '../Billing/vpp/utils';

import styles from './index.module.scss';

export const SignContract = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('SignContract');

  const ecaModalType = useSelector(selectEcaModalType);
  const isLoading = useSelector(selectEcaIsLoading);
  const contractInstance = useSelector(selectContractInstance);
  const { workOrders } = useSelector(selectWorkOrders);
  const { signerUrl } = contractInstance || {};
  const error = useSelector(selectEcaError);
  const hasSeenSuccessScreen = useSelector(selectHasSeenSuccessScreen);
  const isServiceable = useSelector(selectIsFiberServiceable);

  const [hasSigned, setHasSigned] = useState(false);
  const iframePropsWithScrolling = { ...iframeProps, scrolling: 'yes' };
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const showCancelModal = isCancelModalOpen && contractInstance?.contractState.reason === ContractStateReason.Unsigned;

  const { goTo } = useNavigate();

  const resetModalState = () => {
    dispatch(setEcaError(null));
    dispatch(setEcaModalType(null));
  };

  // At refresh or initial load, fetch the contract instance if signerUrl is not available
  useEffect(() => {
    if (!signerUrl) {
      dispatch(getContractInstance());
    }
  }, [signerUrl, dispatch]);

  // When the contract is signed, show the success screen and track it
  useEffect(() => {
    if (
      ecaModalType === EcaModalType.Success &&
      contractInstance?.contractState.reason === ContractStateReason.Signed &&
      !hasSeenSuccessScreen
    ) {
      dispatch(setHasSeenSuccessScreen(true));
    }
  }, [ecaModalType, contractInstance, hasSeenSuccessScreen, dispatch]);

  // Redirect to home if success screen was seen and not currently showing success modal
  useEffect(() => {
    if (!hasSigned && hasSeenSuccessScreen && contractInstance?.contractState.reason === ContractStateReason.Signed) {
      goTo(ROUTES.HOME);
    }
  }, [ecaModalType, contractInstance, hasSeenSuccessScreen, hasSigned]);

  // Reset hasDispatched on unmount
  useEffect(() => {
    return () => {
      hasDispatched.current = false; // Reset on unmount
    };
  }, []);

  // Use a ref to ignore the first onLoad and prevent repeated dispatch
  const isFirstLoad = useRef(true);
  const hasDispatched = useRef(false);

  const handleIframeLoad = (event: React.ChangeEvent<HTMLIFrameElement>) => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    try {
      const iframeLocation = event?.target?.contentWindow?.location.href;
    } catch (e) {
      console.error('SecurityError: Cannot access content location due to cross-origin policy.');
      if (!hasDispatched.current) {
        setHasSigned(true);
        hasDispatched.current = true;
        dispatch(getContractInstance());
      }
    }
  };

  if (isLoading) {
    return <EcaLoadingState description={t('loadingModal.description')} />;
  }

  if (ecaModalType === EcaModalType.Success) {
    const hasSchedule = workOrders?.some((wo) => wo.schedule !== null && wo.schedule !== undefined);

    const showScheduleButton = !hasSchedule && isServiceable;

    return (
      <EcaSuccessModal
        isOpen={true}
        showScheduleButton={showScheduleButton}
        handleSchedule={() => goTo(ROUTES.SCHEDULE_INSTALLATION)}
        isServiceable={isServiceable}
        t={t}
      />
    );
  }

  if (error?.message) {
    return (
      <EcaErrorModal
        isOpen={!!error}
        onClose={() => {
          resetModalState();
        }}
        onRetry={() => {
          resetModalState();
          dispatch(setEcaIsLoading(true));
          dispatch(getContractInstance());
        }}
      />
    );
  }

  return (
    <Surface className={styles['main-container']} variant={'secondary'} data-cy={'sign-contract-main-container'}>
      <Surface variant={'secondary'} className={styles['header-container']}>
        <Surface variant={'secondary'} className={styles['header-text']}>
          <Txt variant={'heading3'} mb={'8px'} data-cy={'sign-contract-heading'}>
            {t('title')}
          </Txt>
          <Txt
            variant={'bodyLargeRegular'}
            color={'subtle'}
            component={'p'}
            mb={{ lg: '64px', md: '32px' }}
            data-cy={'sign-contract-subheading'}
          >
            {t('subtitle')}
          </Txt>
        </Surface>
        <Illustration />
      </Surface>

      <Surface className={styles['iframe-container']} variant={'secondary'}>
        <iframe
          title="Sign Electronic Contract"
          aria-label={t('iframeAriaLabel')}
          src={signerUrl}
          className={styles['iframe-element']}
          data-cy={'sign-contract-iframe'}
          {...iframePropsWithScrolling}
          onLoad={handleIframeLoad}
        />
      </Surface>

      <Surface variant={'secondary'} className={styles['button-container']}>
        <Button
          fluid
          mt={{ lg: '32px', xs: '24px' }}
          variant={'secondary'}
          data-cy={'sign-contract-cancel-btn'}
          onClick={() => setIsCancelModalOpen(true)}
        >
          {t('cancelButton')}
        </Button>
        {showCancelModal && (
          <CancelSignEcaModal
            isOpen={isCancelModalOpen}
            isServiceable={isServiceable}
            handleBack={() => setIsCancelModalOpen(false)}
            handleExit={() => {
              setIsCancelModalOpen(false);
              goTo(ROUTES.HOME);
            }}
          />
        )}
      </Surface>
    </Surface>
  );
};
